import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import moment from 'moment';
import React, { useState } from 'react';
import { StyleProp, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX } from '@fortawesome/free-solid-svg-icons';
import { Hoverable } from 'react-native-web-hover';
import { CustomTooltip } from './CustomTooltip';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { translate } from '../services/translate';
import inputStylesheet from '../styles/inputStyles';

export type ValuePiece = Date | null;
export type Value = ValuePiece | [ValuePiece, ValuePiece];

type Props = {
    maxDate?: Date;
    minDate?: Date;
    zIndex?: number;
    hideClearButton?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    labelFontSize?: number;
    disabled?: boolean;
} & ({
    useCustomDateInfo?: false;
    dateFormat?: undefined;
    label?: string;
    hintText?: string;
    required?: boolean;
    allowSingleValue?: boolean;
    viewStyle?: StyleProp<ViewStyle>;
    hintTextConfig?: {
        tooltipStyle?: React.CSSProperties;
        tooltipDirection?: 'right' | 'left' | 'top';
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;

    /**
     * this field should have the format {from: Date | null, to:Date | null}
     */
    fieldName: string;
} | {
    useCustomDateInfo: true;
    calendarPosition?: 'right' | 'left';
    dateFormat?: string;
    dateRangePickerStyle?: StyleProp<ViewStyle>;
    dateFrom: ValuePiece;
    setDateFrom: React.Dispatch<React.SetStateAction<ValuePiece>>;
    dateTo: ValuePiece;
    setDateTo: React.Dispatch<React.SetStateAction<ValuePiece>>;
    onChange?: (value: Value) => void;
    onRemove?: () => void;
});

export default function MyDateRangePicker(props: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const [hintHovered, setHintHovered] = useState(false);

    const [internalZIndex, setInternalZIndex] = useState(props.zIndex);

    function dateInfo() {
        if (!props.useCustomDateInfo) {
            return <></>;
        }
        return <>
            <MyAppText style={styles.dateRangePickerText(props.dateFrom != null && props.dateTo != null)}>
                {props.dateFrom
                    ? moment(props.dateFrom).format(props.dateFormat)
                    : props.dateFormat
                } - {props.dateTo
                    ? moment(props.dateTo).format(props.dateFormat)
                    : props.dateFormat
                }
            </MyAppText>
            {props.dateFrom && props.dateTo && <TouchableOpacity onPress={props.onRemove}>
                <FontAwesomeIcon fontSize={12} icon={faX} color={theme.colors.errors['500']} />
            </TouchableOpacity>
            }
        </>;
    }

    if (props.useCustomDateInfo) {
        return <View style={[styles.dateRangePicker, styles.container({ zIndex: 3 }), props.dateRangePickerStyle]}>
            {props.calendarPosition === 'right' && dateInfo()}
            <DateRangePicker
                calendarProps={{ locale: 'pt-BR' }}
                maxDate={props.maxDate}
                minDate={props.minDate}
                onChange={(value: Value) => {
                    if (Array.isArray(value)) {
                        props.setDateFrom(value[0]);
                        props.setDateTo(value[1]);
                        props.onChange?.(value);
                    }
                }}
                autoFocus={false}
                shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                value={[props.dateFrom, props.dateTo]}
                clearIcon={null}
                locale='pt-BR'
                className={UnistylesRuntime.themeName === 'dark' ? 'basic_calendar dark' : 'basic_calendar light'}
            />
            {props.calendarPosition === 'left' && dateInfo()}
        </View>;
    }

    const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

    rules.validate = {
        toMustBeGreaterThanFrom: v => v.from <= v.to || translate('toMustBeGreaterThanFrom'),
    };

    if (props.required) {
        rules.required = translate('required_field');
        rules.validate.requiredToAndFrom = v => v.from !== null && v.to !== null || translate('required_field');
    }

    if (!props.allowSingleValue) {
        rules.validate.notAllowSingleValue = v => (v.from === null) === (v.to === null) || translate('notAllowSingleValue');
    }


    return <Controller
        control={props.form.control}
        name={props.fieldName}
        rules={rules}
        render={({ field, fieldState }) => (
            <View style={[styles.container({ zIndex: internalZIndex }), hintHovered && { zIndex: 9999 }, props.viewStyle]}>
                <View style={[{ flexDirection: 'row', columnGap: 5 }, props.hintText ? { zIndex: 9999 } : {}]}>
                    {props.label && <MyAppText style={props.labelStyle ? props.labelStyle : styles.label(props.labelFontSize ?? 16)}>{props.label}</MyAppText>}
                    {props.required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                    {props.hintText
                        ? <Hoverable>
                            {({ hovered }) => {
                                setHintHovered(hovered);
                                return <CustomTooltip
                                    content={props.hintText}
                                    tooltipStyle={props.hintTextConfig?.tooltipStyle}
                                    tooltipDirection={props.hintTextConfig?.tooltipDirection}
                                />;
                            }}
                        </Hoverable>
                        : null
                    }
                </View>
                <DateRangePicker
                    calendarProps={{ locale: 'pt-BR' }}
                    maxDate={props.maxDate}
                    minDate={props.minDate}
                    onBlur={field.onBlur}
                    onInvalidChange={() => {
                        props.form.setError(props.fieldName, { type: 'manual', message: translate('invalidDate') });
                    }}
                    onChange={(value: Value) => {
                        props.form.clearErrors(props.fieldName);

                        if (value === null) {
                            return field.onChange({ from: null, to: null, });
                        }

                        if (Array.isArray(value)) {
                            field.onChange({ from: value[0], to: value[1], });
                        }
                    }}
                    onCalendarOpen={() => setInternalZIndex(99999)}
                    onCalendarClose={() => setInternalZIndex(props.zIndex)}
                    autoFocus={false}
                    shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                    value={[field.value.from, field.value.to]}
                    clearIcon={props.hideClearButton ? null : undefined}
                    locale='pt-BR'
                    className={[
                        fieldState.invalid ? 'react-daterange-picker--invalid' : undefined,
                        ['light', 'lightAlt'].includes(UnistylesRuntime.themeName)
                            ? `inputColorLight${props.disabled ? 'Disabled' : ''}`
                            : `inputColorDark${props.disabled ? 'Disabled' : ''}`
                    ]}
                    disabled={props.disabled}
                />
                {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
            </View>
        )}
    />;
}

const styleSheet = createStyleSheet((theme) => ({
    dateRangePicker: {
        borderRadius: 4,
        borderWidth: 1.25,
        borderColor: theme.colors.primary['500'],
        paddingHorizontal: 16,
        paddingVertical: 8,
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center',
        backgroundColor: theme.colors.white['500']
    },
    dateRangePickerText: (hasValue: boolean) => ({
        fontSize: 16,
        color: theme.colors.neutral[hasValue ? '400' : '300'],
        fontWeight: '600'
    }),
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
    }),
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
    container: ({ zIndex }: { zIndex?: number; }) => {
        const { styles: defaultInputStyle } = useStyles(inputStylesheet);
        const placeholder = defaultInputStyle.input({ placeholder: true });
        const defaultInput = defaultInputStyle.input({});
        const invalid = defaultInputStyle.input({ invalid: true });

        return {
            zIndex,
            '--daterange-placeholder-color': placeholder.color,
            '--daterange-invalid-border-color': invalid.borderColor,
            '--daterange-text-color': defaultInput.color,
            '--daterange-border-color': defaultInput.borderColor,
            '--daterange-button-color': defaultInput.borderColor,
            '--daterange-border-radius': '6px',
            '--daterange-font-size': '14px',
            '--daterange-font-weight': '500',

            '--daterange-calendar-background-color': defaultInput.backgroundColor,
            '--daterange-calendar-border-color': defaultInput.borderColor,
            '--daterange-calendar-days-text-color': theme.colors.calendar.dayText,
            '--daterange-calendar-navigation-text-color': theme.colors.calendar.navigationText,
            '--daterange-calendar-navigation-text-disabled-color': theme.colors.calendar.navigationDisabledText,
            '--daterange-calendar-navigation-background-hover-color': theme.colors.calendar.navigationBackgroundHover,
            '--daterange-calendar-days-background-selected-active-color': theme.colors.calendar.dayBackgroundSelectedActive,
            '--daterange-calendar-days-background-selected-range-color': theme.colors.calendar.dayBackgroundSelectedRange,
            '--daterange-calendar-days-text-selected-range-color': theme.colors.calendar.dayTextSelectedRangeColor,
            '--daterange-calendar-days-text-disabled-color': theme.colors.calendar.dayTextDisabledColor,
        };
    },
}));
